import { useContext, useState } from 'react';
import { Button, Text, useToast } from '@chakra-ui/react';
import CustomToast from '../../../common/CustomToast';
import ConfirmModal from '../../../common/ConfirmModal';
import CustomCheckbox from '../../common/CustomCheckbox';
import { CustomSquareDownloadIcon } from '../../../theme/icons/CustomSquareDownloadIcon';
import LiveEventDataListContext from '../../../store/client/LiveEventDataListContext';
import { MeContext } from '../../../context/MeContext';
import type { MeContextState } from '../../../store/types/account';
import { useExportScansMutation } from '../../../store/redux/api/scanApi';
import { scanFilterParams } from './scanFilterParams';

const exportErrorPayload = (error: unknown) => {
  const e = error as {
    status?: number;
    data?: { message?: string; errors?: string[]; code?: number } | string;
  };
  const data =
    typeof e?.data === 'object' && e.data !== null ? e.data : undefined;

  return {
    code: data?.code ?? e?.status ?? null,
    message: data?.message ?? data?.errors?.[0] ?? null,
  };
};

const LiveEventDataListExport = () => {
  const { filterValues } = useContext(LiveEventDataListContext);
  const meCtx = useContext(MeContext) as { state: MeContextState };
  const { filterEntity } = meCtx.state;
  const [isOpen, setIsOpen] = useState(false);
  const [includePassFields, setIncludePassFields] = useState(false);
  const [exportScans, { isLoading: isExporting }] = useExportScansMutation();
  const toast = useToast();

  const email = meCtx.state.account?.email?.trim();

  const openDialog = () => {
    setIncludePassFields(false);
    setIsOpen(true);
  };

  const handleExport = async () => {
    try {
      await exportScans({
        entityUuid: filterEntity?.uuid,
        email,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        includePassFields,
        filters: scanFilterParams(filterValues),
      }).unwrap();

      setIsOpen(false);
      toast({
        render: ({ onClose }) => (
          <CustomToast
            status="info"
            title="Please check your email"
            description={`A download link has been sent to ${email}. It may take a few minutes to arrive.`}
            onClose={onClose}
          />
        ),
      });
    } catch (error) {
      const { message, code } = exportErrorPayload(error);
      code !== 401 &&
        toast({
          render: (props) => (
            <CustomToast
              status="error"
              title={message ? message : 'Something went wrong'}
              description={!message && 'Please try again later'}
              onClose={props.onClose}
            />
          ),
        });
    }
  };

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        leftIcon={<CustomSquareDownloadIcon boxSize={4} />}
        onClick={openDialog}
      >
        Export
      </Button>
      <ConfirmModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Export scans"
        description={
          <>
            We will email a download link to{' '}
            <Text as="span" color="orangePrimary">
              {email}
            </Text>
            . The file will contain all the scans currently listed.
          </>
        }
        primaryButton={{
          label: 'Export',
          onClick: handleExport,
          isLoading: isExporting,
          loadingText: 'Exporting',
        }}
        secondaryButton={{
          label: 'Cancel',
          onClick: () => setIsOpen(false),
        }}
      >
        <CustomCheckbox
          label="Include pass fields"
          description="Adds a column for each field stored on the scanned pass, such as the holder's name or seat number."
          isChecked={includePassFields}
          onChange={(e) => setIncludePassFields(e.target.checked)}
        />
      </ConfirmModal>
    </>
  );
};

export default LiveEventDataListExport;
